import { Component, computed, inject, model, signal } from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { ActivatedRoute, Router } from '@angular/router';
import { forkJoin } from 'rxjs';
import { LoadingComponent } from 'src/app/components/loading/loading.component';
import { ApplicationApiService, RoadmapDto, RoadmapTargetDto, CurrentConditionDto, TargetCategoryDto, FileResponse, ProjectDto, ApproverDto } from 'src/app/services/application-api.service';
import { BreadCrumbItem, KENDO_NAVIGATION } from '@progress/kendo-angular-navigation';
import { faUserCheck, faDraftingCompass, faAngleDoubleRight, faUnlink, faLink, faSearch, faAngleDoubleLeft, faExclamationCircle, faFileAlt, faHistory } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatFormField } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatButtonModule } from '@angular/material/button';
import { MatChipsModule } from '@angular/material/chips';
import { MatSliderModule } from '@angular/material/slider';
import { DatePipe, DecimalPipe } from '@angular/common';
import { KENDO_CHARTS } from '@progress/kendo-angular-charts';
import { ControlFunction, IRoadmapControlFunction, RoadmapControlsComponent } from './roadmap-controls/roadmap-controls.component';
import { SnackBarService, UserProfileService } from 'services';
import { VolumeLSR } from 'src/app/types/constants';
import { RoadmapGoalsComponent } from './roadmap-goals/roadmap-goals.component';
import { InfoboxComponent } from 'src/app/components/infobox/infobox.component';
import { SearchProjectComponent } from './search-project/search-project.component';
import { MatDialog } from '@angular/material/dialog';
import { ChartComponent } from '@progress/kendo-angular-charts';
import { ViewChild } from '@angular/core';
import { saveAs } from 'file-saver';

@Component({
  selector: 'app-roadmap',
  imports: [LoadingComponent, KENDO_NAVIGATION, FontAwesomeModule, ReactiveFormsModule, MatFormField, MatInputModule, MatSelectModule, MatCheckboxModule, MatTooltipModule, MatButtonModule,
    MatChipsModule, MatSliderModule, FormsModule, DecimalPipe, DatePipe, KENDO_CHARTS, RoadmapControlsComponent, RoadmapGoalsComponent],
  templateUrl: './roadmap.component.html',
  styleUrl: './roadmap.component.scss'
})
export class RoadmapComponent {
  // Inject dependencies
  private readonly apiService = inject(ApplicationApiService);
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly userProfileService = inject(UserProfileService);
  private readonly dialog = inject(MatDialog);
  private readonly snackbar = inject(SnackBarService);
  @ViewChild('chartRef') chart!: ChartComponent;

  // Signals for component state
  readonly isLoading = signal(false);
  readonly loadingMsg = signal("Loading Roadmap");
  readonly inEditMode = signal(false);
  readonly showFatalErrorApprover = signal(false);
  readonly roadmap = signal<RoadmapDto | undefined>(undefined);
  readonly currentConditions = signal<CurrentConditionDto[]>([]);
  readonly versionSliderValue = signal(1);
  readonly chartData = signal<any[]>([]);
  readonly breadcrumbs = signal<BreadCrumbItem[]>([
    { text: "Region Map", title: "Region Map" }
  ]);

  // Convert Observable to signal
  readonly appUser = toSignal(this.userProfileService.user$);

  // Computed signal for derived state
  readonly volumeOver10M = computed(() => {
    const rm = this.roadmap();
    return rm?.revenue ? rm.revenue > VolumeLSR : false;
  });

  // Icons (static, no need for signals)
  readonly faUserCheck = faUserCheck;
  readonly faDraftingCompass = faDraftingCompass;
  readonly faAngleDoubleRight = faAngleDoubleRight;
  readonly faUnlink = faUnlink;
  readonly faLink = faLink;
  readonly faSearch = faSearch;
  readonly faAngleDoubleLeft = faAngleDoubleLeft;
  readonly faExclamationCircle = faExclamationCircle;
  readonly faFileAlt = faFileAlt;
  readonly faHistory = faHistory;

  // project variables
  readonly projectName = model('');
  readonly projectNumber = model('');
  readonly leanChampionName = model('');
  readonly isProjectNumberTbd = model(false);
  readonly selectedApprover = model<ApproverDto | null>(null);
  readonly approvers = signal<ApproverDto[]>([]);

  constructor() {
    this.loadRoadmapData();
  }

  private loadRoadmapData(): void {
    this.isLoading.set(true);

    this.route.paramMap.pipe(takeUntilDestroyed()).subscribe(params => {
      const roadmapId = Number(params.get('id'));
      const version = Number(params.get('version'));

      forkJoin({
        roadmap: version ? this.apiService.apiRoadmapVersion(roadmapId, version) : this.apiService.apiRoadmapGet(roadmapId),
        currentConditions: this.apiService.apiCurrentConditions()
      }).subscribe({
        next: ({ roadmap, currentConditions }) => {
          this.roadmap.set(roadmap);
          this.currentConditions.set(currentConditions);

          // list of approvers for the roadmap's profit center
          this.apiService.apiProfitCenterApprovers(roadmap.profitCenter.profitCenterCode!).subscribe((approvers: ApproverDto[]) => {
            this.approvers.set(approvers);
          });

          // breadcrumbs
          this.breadcrumbs.set([
            { text: "Region Map", title: "Region Map" },
            { text: 'Region: ' + roadmap.profitCenter.region!.regionName, title: 'Region: ' + roadmap.profitCenter.region!.regionName },
            { text: roadmap.projectName!, title: roadmap.projectName! }
          ]);

          // Create chart
          this.createChart(roadmap.targetCategories!);
          this.versionSliderValue.set(roadmap.version!);

          this.projectName.set(this.roadmap()!.projectName ?? '');
          this.projectNumber.set(this.roadmap()!.projectNumber ?? '');
          this.isProjectNumberTbd.set(this.roadmap()!.projectNumber === 'TBD');
          this.leanChampionName.set(this.roadmap()!.leanChampionName ?? '');
          this.selectedApprover.set(this.roadmap()!.approver ? this.roadmap()!.approver! : null);

          this.isLoading.set(false);
        },
        error: () => {
          this.isLoading.set(false);
        }
      });
    });
  }

  createChart(targetCategories: TargetCategoryDto[]): void {
    // get list of all RoadmapTargets
    const targets: RoadmapTargetDto[] = [];
    targetCategories.forEach(category => {
      category.targetConditions?.forEach(condition => {
        condition.roadmapTargets?.forEach(target => {
          targets.push(target);
        });
      });
    });

    this.chartData.set([
      { category: 'Achieved', color: '#a8eb8c', value: targets.filter(r => r.currentCondition?.id === 1).length },
      { category: 'Planned', color: '#4682b4eb', value: targets.filter(r => r.currentCondition?.id === 4).length },
      { category: 'In Progress', color: '#f0e8bb', value: targets.filter(r => r.currentCondition?.id === 2).length },
      { category: 'Not Achieved', color: '#f19d96', value: targets.filter(r => r.currentCondition?.id === 3).length },
      { category: 'Not Planned', color: '#ccccccd0', value: targets.filter(r => r.currentCondition?.id === 0).length }
    ]);
  }

  roadmapSubmitControlFunction(controlFunction: IRoadmapControlFunction): void {
    switch (controlFunction.function) {
      case ControlFunction.Edit:
        this.inEditMode.set(controlFunction.value === 'true');
        break;
      case ControlFunction.Delete:
        this.deleteRoadmap();
        break;
      case ControlFunction.UpdateAdmin:
        //this.updateExistingRoadmapAdmin(+controlFunction.value);
        break;
      case ControlFunction.Update:
        this.updateExistingRoadmap(+controlFunction.value!);
        break;
    }
  }

  private updateExistingRoadmap(statusId: number): void {

  }

  onProjectTbdChange(tbd: boolean): void {
    if (tbd) {
      this.projectNumber.set('TBD');
    } else {
      this.projectNumber.set('');
    }
  }

  deleteRoadmap(): void {
    const dialogRef = this.dialog.open(InfoboxComponent, {
      data: {
        title: `Delete Roadmap`,
        content: 'You are about to delete this roadmap and all of its associated roadmap data.  This data includes comments, goals, file attachments, and other information.  These actions cannot be undone.  Are you sure?',
      }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (!result) {
        return;
      }

      const currentRoadmap = this.roadmap();
      if (!currentRoadmap?.id) return;

      this.apiService.apiRoadmapDelete(currentRoadmap.id).subscribe(() => {
        this.snackbar.success('Roadmap deleted successfully.');
        this.router.navigate(['/region', currentRoadmap.profitCenter.region!.id]);
      });
    });
  }

  onSearchClick(): void {
    const dialogRef = this.dialog.open(SearchProjectComponent, {
      height: '600px',
      width: '1000px'
    });

    dialogRef.afterClosed().subscribe((result: ProjectDto | null) => {
      if (!result) {
        return;
      }

      this.projectName.set(result.projectName!);
      this.projectNumber.set(result.projectNumber ?? '');
      this.isProjectNumberTbd.set(false);
      this.roadmap.set({ ...this.roadmap()!, opportunityId: result.opportunityId });
      this.roadmap.set({ ...this.roadmap()!, marketSegment: result.primaryBuildingType });
      this.roadmap.set({ ...this.roadmap()!, profitCenter: { profitCenterName: result.profitCenterName!, profitCenterCode: result.profitCenterCode! } });
      this.roadmap.set({ ...this.roadmap()!, revenue: result.eccRevenue ?? result.gsrRevenue! });
    });
  }

  onVersionChange(): void {
    if (this.versionSliderValue() === this.roadmap()!.maxVersion) {
      this.router.navigate(['/roadmap', this.roadmap()!.id]);
      return;
    }
    this.router.navigate(['/roadmap', this.roadmap()!.id, this.versionSliderValue()]);
  }

  onBreadcrumbClick(item: BreadCrumbItem): void {
    if (item.text === "Region Map") {
      this.router.navigate(['/home']);
    }

    if (item.text?.startsWith("Region:")) {
      const currentRoadmap = this.roadmap();
      if (currentRoadmap) {
        this.router.navigate(['/region', currentRoadmap.profitCenter.region!.id]);
      }
    }
  }

  handlePdfAction(action: 'download' | 'print') {
    if (action === 'download') {
      this.exportPdf(false);
    } else {
      this.exportPdf(true);
    }
  }

  async exportPdf(print: boolean): Promise<void> {
    if (!this.chart) {
      console.error('Chart not ready');
      return;
    }

    this.loadingMsg.set("Exporting PDF...");
    this.isLoading.set(true);

    const raw = await this.chart.exportImage({
      width: 800,
      height: 600
    });

    const image = raw.startsWith('data:image')
      ? raw
      : `data:image/png;base64,${raw}`;

    const filename = 'Lean Roadmap' + Date.now() + '.pdf';

    this.apiService.apiExportExportPdf({
      roadmapId: this.roadmap()!.id!,
      version: this.roadmap()!.version!,
      chartImage: image
    }).subscribe({
      next: (res: FileResponse) => {

        const blob = new Blob([res.data], { type: 'application/pdf' });
        const url = URL.createObjectURL(blob);
        if (print) {
          const win = window.open(url);
          win?.addEventListener('load', () => win.print());
        } else {
          saveAs(blob, filename);
        }
        this.isLoading.set(false);
        this.loadingMsg.set("Loading Roadmap");
      },
      error: () => {
        this.isLoading.set(false);
        this.loadingMsg.set("Loading Roadmap");
      }
    });
  }
}
